import React from 'react';
import { RadioGroup, Radio } from '@cad-arc';
import { CommonControlProps, IntegratedFormControl } from './integrated-form-control';
import { FieldPath, FieldValues } from 'react-hook-form';
import { mapInputSizeToCommonSize } from './utils';

type BooleanOption = {
    label: string;
    value: boolean;
};

type RadioGroupProps = React.ComponentProps<typeof RadioGroup>;
type Props<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = CommonControlProps<TFieldValues, TName> & {
    radioGroupProps?: Partial<RadioGroupProps>;
    defaultValue?: boolean | undefined;
    items: BooleanOption[];
};

const renderViewMode = (value: BooleanOption[]) => {
    return value.map((option) => option.label).join(', ');
};

export function FormBooleanRadioGroup<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: Props<TFieldValues, TName>): React.ReactElement {
    const { radioGroupProps, items, ...integratedFormControlProps } = props;
    const radioGroupPropsMemo = React.useMemo(() => radioGroupProps ?? {}, [radioGroupProps]);

    // Convert boolean items to string items for RadioGroup
    const stringItems = React.useMemo(() => {
        return items.map((item) => ({
            ...item,
            value: String(item.value),
        }));
    }, [items]);

    return (
        <IntegratedFormControl {...integratedFormControlProps} renderViewMode={renderViewMode}>
            {(inputProps) => {
                // Convert form boolean value to string for RadioGroup display
                const displayValue = String(inputProps.value);

                // Handle change: convert string back to boolean for form
                const handleChange = (value: string | number) => {
                    const booleanValue = value === 'true';
                    inputProps.onChange(booleanValue);
                };

                return (
                    <RadioGroup
                        {...mapInputSizeToCommonSize(inputProps)}
                        {...radioGroupPropsMemo}
                        value={displayValue}
                        onChange={handleChange}
                    >
                        {stringItems.map((item) => (
                            <Radio key={item.label} value={item.value}>
                                {item.label}
                            </Radio>
                        ))}
                    </RadioGroup>
                );
            }}
        </IntegratedFormControl>
    );
}
